import React, { useState, useEffect, useMemo } from "react";

import {
  Database,
  UserCheck,
  UserX,
  SquarePen,
  MoreVertical,
  ChevronRight,
  ChevronDown,
  Plus,
  FilterX,
} from "lucide-react";

import AddExecutive from "./AddExecutive";
import AccountMetricsRow from "../../Components/AccountMetricsRow";
import Button from "../../Components/Button";
import DropdownSelector from "../../Components/DropdownSelector";
import SearchInput from "../../Components/Search/SearchInput";
import SidebarModal from "../../Components/SidebarModal";
import TabsComponent from "../../Components/Tab";
import Table from "../../Components/Table";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchExecutiveSponsors,
  Executive,
  ExecutiveSponsorsFilters,
} from "../../Store/Reducers/OpsSlice";
import {
  fetchColorCodingThresholds,
  fetchVendorAdminGrades,
} from "../../Store/Reducers/VendorAdminSlice";
import { useAccountTiers } from "../../Utils/Helper/accountTierHelpers";
import { formatCompactCurrency } from "../../Utils/Helper/formatCompactCurrency";
import { getInitials, searchByName, diffTime } from "../../Utils/Helper/Helper";
import { formatDateUS } from "../../Utils/Helper/Helper";
import AccountsDetailsModal from "../AccountsDetailsModal";

import "./style.scss";

// Component Data Types
export interface SponsorAccount {
  name: string;
  initials: string;
  level: string;
  sponsorScore: string;
  sponsorStartDate: string;
  arr: string;
  nrr: string;
  csat: string;
  nps: string;
}

export interface SponsorData {
  utilization: any;
  id: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  city: string | null;
  country: string | null;
  region: string;
  initials: string;
  grade: number;
  gradeLabel: string;
  experienceLevel: string | null;
  accounts: number; // accountLimit from API
  serving: number; // current_accounts from API
  availableCapacity: number;
  status: string;
  sphereOfExcellence: string;
  specialization: string | null;
  certifications: string[];
  lastActivity: string;
  languages: string[];
  isActive: boolean;
  availabilityStatus: string | null;
  sponsorAccounts: SponsorAccount[];
}

const ExecutiveSponsorsContainer = () => {
  const dispatch = useAppDispatch();
  const { formatTier } = useAccountTiers();
  const { data: apiData, loading } = useAppSelector(
    state => state.ops.executiveSponsors
  );
  const { colorCodingThresholds, grades } = useAppSelector(
    (state: any) => state.vendorAdmin
  );

  const [activeSponsorFilter, setActiveSponsorFilter] = useState<string | null>(
    ""
  );
  const [selectedGrade, setSelectedGrade] = useState<string | null>("");
  const [selectedStatus, setSelectedStatus] = useState<string | null>("");
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [isAddExecutiveOpen, setIsAddExecutiveOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [editExecutive, setEditExecutive] = useState<SponsorData | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [viewSponsor, setViewSponsor] = useState<SponsorData | null>(null);
  const [viewAccount, setViewAccount] = useState<SponsorAccount | null>(null);

  const toggleExpanded = (id: string) =>
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));

  // Fetch color coding thresholds and grades on mount
  useEffect(() => {
    dispatch(fetchColorCodingThresholds());
    dispatch(fetchVendorAdminGrades());
  }, [dispatch]);

  // Fetch executive sponsors data when assigned_status filter changes (API filter)
  useEffect(() => {
    const filters: ExecutiveSponsorsFilters = {};
    if (activeSponsorFilter) {
      filters.assigned_status = activeSponsorFilter as
        | "assigned"
        | "unassigned";
    }
    dispatch(fetchExecutiveSponsors(filters));
  }, [dispatch, activeSponsorFilter]);

  const handleSponsorFilterChange = (tabId: string) => {
    setActiveSponsorFilter(tabId);
  };

  const handleResetFilters = () => {
    setActiveSponsorFilter("");
    setSelectedGrade("");
    setSelectedStatus("");
    setSearchQuery(null);
  };

  const sponsorFilterTabs = [
    { id: "", label: "All Sponsors", icon: <Database size={18} /> },
    {
      id: "assigned",
      label: "Assigned Sponsors",
      icon: <UserCheck size={18} />,
    },
    {
      id: "unassigned",
      label: "Not Assigned Sponsors",
      icon: <UserX size={18} />,
    },
  ];

  const gradeOptions = useMemo(() => {
    const options = [{ label: "All Grades", value: "" }];
    if (grades?.items && Array.isArray(grades.items)) {
      grades.items.forEach((item: any) => {
        options.push({
          label: item.grade_name,
          value: String(item.grade ?? item.grade_name),
        });
      });
    }
    return options;
  }, [grades]);

  const statusOptions = [
    { label: "All Status", value: "" },
    { label: "Active", value: "active" },
    { label: "Bench", value: "bench" },
  ];

  const formatCurrency = (value: number | null | undefined): string =>
    formatCompactCurrency(value, { fallback: "N/A" });

  // Helper function to determine sphere of excellence based on performance score
  const getSphereOfExcellence = (score?: number | null): string => {
    if (!score) return "Not Available";
    if (score >= 90) return "High";
    if (score >= 70) return "Medium";
    return "Low";
  };

  // Helper function to determine status
  const getStatus = (executive: Executive): string => {
    // Status is based on serving (current_accounts)
    // If serving is 0, it's Bench, otherwise it's Active
    return executive.current_accounts === 0 ? "Bench" : "Active";
  };

  // Helper function to get status badge styling
  const getStatusBadge = (status: string): string => {
    const statusConfig: Record<string, string> = {
      Active: "bg-green-100 text-green-800 border-green-200",
      Bench: "bg-blue-100 text-blue-800 border-blue-200",
    };
    return statusConfig[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  // Map API response to component data structure
  const mapExecutiveToSponsorData = (executive: Executive): SponsorData => {
    const availableCapacity =
      (executive.accountLimit || 0) - executive.current_accounts;
    const status = getStatus(executive);
    const sphereOfExcellence = getSphereOfExcellence(
      executive.performanceScore
    );
    const lastActivity = executive.updatedAt
      ? diffTime(executive.updatedAt)
      : "N/A";

    // Map accounts to sponsorAccounts
    const sponsorAccounts: SponsorAccount[] = executive.accounts.map(acc => ({
      name: acc.name,
      initials: getInitials(acc.name),
      level: acc.tier ? formatTier(acc.tier) || acc.tier : "N/A",
      sponsorScore: executive.performanceScore
        ? `${(executive.performanceScore / 10).toFixed(1)}/10`
        : "N/A",
      sponsorStartDate: acc.assigned_on
        ? formatDateUS(acc.assigned_on).split(",")[0]
        : "N/A",
      arr: formatCurrency(acc.arr),
      nrr: acc.nrr ? `${acc.nrr}` : "N/A",
      csat: acc.csat ? `${acc.csat}/10` : "N/A",
      nps: acc.nps ? acc.nps.toString() : "N/A",
    }));

    return {
      utilization: executive.utilization,
      id: executive.id,
      name: executive.name,
      title: executive.title || "N/A",
      email: executive.email,
      phone: executive.phone,
      city: executive.city,
      country: executive.country,
      region: executive.region || "N/A",
      initials: getInitials(executive.name),
      grade: executive.grade,
      gradeLabel: executive.gradeLabel,
      experienceLevel: executive.experienceLevel,
      accounts: executive.accountLimit || 0,
      serving: executive.current_accounts,
      availableCapacity: Math.max(0, availableCapacity),
      status,
      sphereOfExcellence,
      specialization: executive.specialization ?? null,
      certifications: executive.certifications ?? [],
      lastActivity,
      languages: executive.languages || [],
      isActive: executive.isActive,
      availabilityStatus: executive.availabilityStatus,
      sponsorAccounts,
    };
  };

  // Transform API data to component format
  const sponsorsDataRaw: SponsorData[] = useMemo(() => {
    if (!apiData?.executives) return [];
    return apiData.executives.map(mapExecutiveToSponsorData);
  }, [apiData]);

  // Filter by search, grade, and status (all client-side)
  const filteredSponsorsData = useMemo(() => {
    let filtered = sponsorsDataRaw;

    if (searchQuery?.trim()) {
      filtered = searchByName(filtered, searchQuery, "name");
    }
    if (selectedGrade) {
      const gradeNum = Number(selectedGrade);
      filtered = filtered.filter(
        s => s.grade === gradeNum || s.gradeLabel === selectedGrade
      );
    }
    if (selectedStatus) {
      filtered = filtered.filter(
        s => s.status.toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    return filtered;
  }, [sponsorsDataRaw, searchQuery, selectedGrade, selectedStatus]);

  // Helper function to get sponsor detail fields for modal
  const getSponsorDetailFields = (sponsor: SponsorData | null) => {
    if (!sponsor) return [];

    return [
      {
        label: "Name",
        value: sponsor.name || "N/A",
        key: "name",
      },
      {
        label: "Region",
        value: sponsor.region || "N/A",
        key: "region",
      },
      {
        label: "Limit",
        value: sponsor.accounts || 0,
        key: "limit",
      },
      {
        label: "Serving",
        value: sponsor.serving || 0,
        key: "serving",
      },
      {
        label: "Available Capacity",
        value: sponsor.availableCapacity || 0,
        key: "availableCapacity",
      },
      {
        label: "Utilization %",
        value: sponsor.utilization ? `${sponsor.utilization}` : "-",
        key: "utilization",
      },
      {
        label: "Status",
        value: sponsor.status || "N/A",
        key: "status",
      },
      {
        label: "Experience Level",
        value: sponsor.sphereOfExcellence || "N/A",
        key: "experienceLevel",
      },
      {
        label: "Grade",
        value: sponsor.grade || "N/A",
        key: "grade",
      },
      {
        label: "Is Assigned",
        value: sponsor.serving > 0 ? "Yes" : "No",
        key: "isAssigned",
      },
      {
        label: "Accounts",
        value:
          sponsor.sponsorAccounts && sponsor.sponsorAccounts.length > 0
            ? sponsor.sponsorAccounts.map(acc => acc.name).join(", ")
            : "-",
        key: "accounts",
      },
    ];
  };

  // Helper function to get account detail fields for modal (sponsor's assigned account)
  const getAccountDetailFields = (account: SponsorAccount | null) => {
    if (!account) return [];

    return [
      { label: "Account Name", value: account.name || "N/A", key: "name" },
      { label: "Level", value: account.level || "N/A", key: "level" },
      {
        label: "Sponsor Score",
        value: account.sponsorScore || "N/A",
        key: "sponsorScore",
      },
      {
        label: "Sponsor Start Date",
        value: account.sponsorStartDate || "N/A",
        key: "sponsorStartDate",
      },
      { label: "ARR", value: account.arr || "N/A", key: "arr" },
      { label: "NRR", value: account.nrr || "N/A", key: "nrr" },
      { label: "CSAT", value: account.csat || "N/A", key: "csat" },
      { label: "NPS", value: account.nps || "N/A", key: "nps" },
    ];
  };

  // Calculate statistics for cards
  const cardData = useMemo(() => {
    if (!apiData) {
      return [
        { label: "Total Executives", value: 0 },
        { label: "Active", value: 0 },
        { label: "Bench", value: 0 },
        { label: "Avg Utilization", value: "0%" },
      ];
    }

    const stats = apiData.statistics;

    return [
      { label: "Total Executives", value: stats.total_executive_sponsors },
      { label: "Active", value: stats.executive_sponsors_assigned_to_accounts },
      {
        label: "Bench",
        value: stats.executive_sponsors_not_assigned_to_accounts,
      },
      {
        label: "Avg Utilization",
        value: `${stats.percentage_assigned_to_accounts}%`,
      },
    ];
  }, [apiData]);

  return (
    <div className="executive-sponsors-container">
      <div className={"cards-row"}>
        {cardData.map((item, idx) => {
          // Apply color to card value based on label
          const valueColorClass =
            item.label === "Active"
              ? "text-green-800"
              : item.label === "Bench"
                ? "text-blue-800"
                : "primary-color";

          // Apply border color based on label
          const borderClass =
            item.label === "Active"
              ? "border border-green-200"
              : item.label === "Bench"
                ? "border border-blue-200"
                : item.label === "Total Executives"
                  ? "border border-blue-200"
                  : "border border-gray-200";

          return (
            <div key={idx} className={`status-card ${borderClass}`}>
              <div className={"row-content"}>
                <div className={`card-value ${valueColorClass}`}>
                  {item.value}
                </div>
                <div className={"card-label"}>{item.label}</div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Secondary Navigation Tabs */}
      <div className="executive-sponsors-container-content">
        <div className="accounts-navigation mb-4">
          <div className="flex items-center justify-between">
            <TabsComponent
              tabs={sponsorFilterTabs}
              activeTab={activeSponsorFilter}
              onTabChange={handleSponsorFilterChange}
              variant="tag-rounded"
            />
            <div className="flex gap-2 items-center">
              <DropdownSelector
                label="All Grades"
                selected={selectedGrade}
                options={gradeOptions}
                onSelect={setSelectedGrade}
              />
              <DropdownSelector
                label="All Status"
                selected={selectedStatus}
                options={statusOptions}
                onSelect={setSelectedStatus}
              />
              <div>
                <SearchInput
                  placeholder="Search"
                  value={searchQuery || ""}
                  onChange={e => setSearchQuery(e.target.value || null)}
                />
              </div>
              <button
                type="button"
                className="p-2 text-gray-500 hover:text-red-600 border-2 border-gray-300 rounded-full hover:border-red-400 transition-all duration-200"
                title="Clear all filters"
                onClick={handleResetFilters}
                aria-label="Reset filters"
              >
                <FilterX className="w-4 h-4" />
              </button>
              <Button
                variant="primary"
                className="flex items-center gap-2"
                onClick={() => setIsAddExecutiveOpen(true)}
              >
                <Plus size={18} />
                Add Executive Sponsor
              </Button>
            </div>
          </div>
        </div>

        {/* Accounts Table */}
        <div className="accounts-table">
          <Table
            sortable
            columns={[
              {
                key: "name",
                title: "Name",
                sortable: true,
                sortValue: (record: SponsorData) => record.name,
                render: (_: any, record: any) => {
                  const hasAccounts =
                    record.sponsorAccounts && record.sponsorAccounts.length > 0;
                  return (
                    <div className="flex align-items-start gap-2">
                      {activeSponsorFilter !== "unassigned" && (
                        <div
                          className={`flex items-center ${hasAccounts ? "" : "invisible"}`}
                        >
                          <Button
                            className="name"
                            variant="clear"
                            onClick={() => toggleExpanded(record.id)}
                          >
                            {expandedRows[record.id] ? (
                              <ChevronDown className="h-4 w-4 text-gray-600" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-600" />
                            )}
                          </Button>
                        </div>
                      )}
                      <div className="text-sm font-medium text-gray-900">
                        {hasAccounts ? (
                          <Button
                            variant="clear"
                            onClick={() => toggleExpanded(record.id)}
                          >
                            {record.name}
                          </Button>
                        ) : (
                          <span>{record.name}</span>
                        )}
                      </div>
                    </div>
                  );
                },
              },
              {
                key: "region",
                title: "Regions",
                sortable: true,
                sortValue: (record: SponsorData) => record.region,
                render: (value: string) => <span>{value || "N/A"}</span>,
              },
              {
                key: "accounts",
                title: "Limit",
                sortable: true,
                sortValue: (record: SponsorData) => record.accounts,
              },
              {
                key: "serving",
                title: "Serving",
                sortable: true,
                sortValue: (record: SponsorData) => record.serving,
              },
              {
                key: "availableCapacity",
                title: "Available Capacity",
                sortable: true,
                sortValue: (record: SponsorData) => record.availableCapacity,
              },
              {
                key: "utilization",
                title: "Utilization %",
                sortable: true,
                sortValue: (record: SponsorData) =>
                  record.accounts > 0
                    ? (record.serving / record.accounts) * 100
                    : 0,
                render: (_: any, record: SponsorData) => {
                  const utilization =
                    record.accounts > 0
                      ? (record.serving / record.accounts) * 100
                      : 0;
                  const roundedUtil = Math.round(utilization);

                  // Use dynamic thresholds from settings
                  const greenThreshold =
                    colorCodingThresholds?.utilization_percentage
                      ?.greenThreshold || 100.0;
                  const yellowThreshold =
                    colorCodingThresholds?.utilization_percentage
                      ?.yellowThreshold || 120.0;

                  let colorClass = "text-green-600";
                  if (roundedUtil > yellowThreshold) {
                    colorClass = "text-red-600";
                  } else if (roundedUtil > greenThreshold) {
                    colorClass = "text-yellow-600";
                  }

                  return (
                    <span className={`font-semibold ${colorClass}`}>
                      {roundedUtil}%
                    </span>
                  );
                },
              },
              {
                key: "status",
                title: "Status",
                sortable: true,
                sortValue: (record: SponsorData) => record.status,
                render: (value: string) => (
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(
                      value
                    )}`}
                  >
                    {value}
                  </span>
                ),
              },
              {
                key: "experienceLevel",
                title: "Experience Level",
                sortable: true,
                sortValue: (record: SponsorData) => record.experienceLevel,
                render: (value: string) => value,
              },
              {
                key: "actions",
                title: "Details",
                className: "flex justify-center",
                render: (_: any, record: SponsorData) => (
                  <div className="flex justify-center items-center">
                    <Button
                      variant="clear"
                      className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                      onClick={() => {
                        setEditExecutive(record);
                        setIsEditMode(true);
                        setIsAddExecutiveOpen(true);
                      }}
                      aria-label="Edit executive"
                    >
                      <SquarePen size={18} className="text-purple-600" />
                    </Button>
                    <Button
                      variant="clear"
                      className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                      onClick={() => setViewSponsor(record)}
                      aria-label="View executive"
                    >
                      <MoreVertical
                        size={18}
                        strokeWidth={1}
                        className="text-purple-600"
                      />
                    </Button>
                  </div>
                ),
                textalign: "center",
              },
            ]}
            data={filteredSponsorsData}
            loading={loading}
            rowKey={(r: any) => r.id}
            expandedRowKeys={Object.keys(expandedRows).filter(
              id => expandedRows[id]
            )}
            expandedRowRender={(record: any) => {
              const hasAccounts =
                record.sponsorAccounts && record.sponsorAccounts.length > 0;
              if (!hasAccounts) {
                return <div></div>;
              }
              return (
                <div className="expanded-accounts">
                  {(record.sponsorAccounts || []).map(
                    (acc: any, idx: number) => (
                      <div key={idx} className="expanded-account-row">
                        <AccountMetricsRow
                          name={acc.name}
                          initials={acc.initials}
                          level={acc.level}
                          sponsorScore={acc.sponsorScore}
                          sponsorStartDate={acc.sponsorStartDate}
                          arr={acc.arr}
                          nrr={acc.nrr}
                          csat={acc.csat}
                          nps={acc.nps}
                          rightActions={
                            <Button
                              variant="clear"
                              className="action-button p-0"
                              onClick={() => setViewAccount(acc)}
                              aria-label="View account details"
                            >
                              <MoreVertical style={{ height: 24, width: 14 }} />
                            </Button>
                          }
                        />
                      </div>
                    )
                  )}
                </div>
              );
            }}
            scrollable={true}
            maxHeight="calc(100vh - 25.25rem)"
          />
        </div>
      </div>

      <SidebarModal
        isOpen={isAddExecutiveOpen}
        onClose={() => {
          setIsAddExecutiveOpen(false);
          setEditExecutive(null);
          setIsEditMode(false);
        }}
        title={
          isEditMode ? "Edit Executive Sponsor" : "Add Multiple Executives"
        }
        className="add-executive-sidebar"
      >
        <AddExecutive
          onClose={() => {
            setIsAddExecutiveOpen(false);
            setEditExecutive(null);
            setIsEditMode(false);
          }}
          isEditMode={isEditMode}
          executiveToEdit={editExecutive}
        />
      </SidebarModal>

      {/* Executive Sponsor Details Modal */}
      <AccountsDetailsModal
        isOpen={!!viewSponsor}
        onClose={() => setViewSponsor(null)}
        title={`Executive Sponsor Details - ${viewSponsor?.name || ""}`}
        fields={getSponsorDetailFields(viewSponsor)}
      />

      {/* Account Details Modal (for accounts in expanded row) */}
      <AccountsDetailsModal
        isOpen={!!viewAccount}
        onClose={() => setViewAccount(null)}
        title={`Account Details - ${viewAccount?.name || ""}`}
        fields={getAccountDetailFields(viewAccount)}
      />
    </div>
  );
};

export default ExecutiveSponsorsContainer;
